/**
 * Alphabetization keys for personal names — the "file under" name a librarian
 * would use.
 *
 * PORTED VERBATIM from `clients/demo-great-host-composers/src/lib/sort-name.ts`
 * (client direction, 2026-07-20), where it files ~400 composers and poets in
 * production. Copied rather than extracted to a package: it is 130 lines of pure
 * string logic with no dependencies, and the shared-package ladder is triggered
 * opportunistically on the SECOND real consumer rendering the same domain with
 * different styling — this is the second consumer, so the next one is the trigger,
 * not this one (`docs/conventions/shared-package-pattern.md`).
 *
 * The Hymnal 1982 needs it for the reason GHC does: the corpus carries no sortable
 * name field, so "Ralph Vaughan Williams" would otherwise file under R.
 *
 * Naive last-word sorting breaks on the three name shapes this database is full
 * of, so we derive the key instead of hard-coding a lookup per person:
 *
 *   1. **Mononyms** — "Kassia" files under K. Nothing to strip.
 *   2. **Toponymic epithets** — "Julian of Norwich" is not a surname "Norwich";
 *      she files under J. Any `X of Y` name sorts by the given name.
 *   3. **Particle surnames** — "van/von/de/della/…". Which word you file under
 *      is a *national* convention, not a grammatical one, and the two rules
 *      point opposite ways:
 *        - Dutch/German/French keep the particle out of the key —
 *          "Margaretha Christina de Jong" files under **J**, "Maria Theresia
 *          von Paradis" under **P**.
 *        - Anglicized (esp. American) names absorb the particle into the
 *          surname — "Patricia Van Ness" files under **V**, not N.
 *
 * The signal that separates them is already written into the name: **an
 * anglicized particle is capitalized, a continental one is not.** "Van Ness" vs
 * "van der Zwan". That is the standard cataloging heuristic (Chicago 16.71–16.84,
 * and how LoC name authorities read), and it resolves every particle name in
 * this catalog correctly without a per-person exception.
 *
 * `IRREGULAR` is reserved for names the rules genuinely cannot reach — currently
 * one: Hildegard von Bingen, whose "von Bingen" is toponymic (a place) wearing
 * the costume of a particle surname, so no capitalization rule can catch it.
 */

/** Nobiliary particles / tussenvoegsels, matched case-insensitively. */
const PARTICLES = new Set([
  'von',
  'van',
  'de',
  'del',
  'della',
  'der',
  'den',
  'di',
  'da',
  'do',
  'dos',
  'das',
  'du',
  'la',
  'le',
  'te',
  'ten',
  'ter',
  'af',
  'av',
  'zu',
])

/** Honorifics that precede the given name and never carry the sort. */
const TITLES = new Set(['mother', 'sister', 'saint', 'st', 'st.', 'dame', 'sr', 'sr.', 'madame', 'mme', 'mme.'])

/** Generational suffixes, stripped before the surname is read. */
const SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'i', 'ii', 'iii', 'iv', 'v'])

/**
 * Names no rule can derive. Keep this as close to empty as possible — an entry
 * here is a claim that the name is genuinely irregular, not that the deriver is
 * incomplete.
 */
const IRREGULAR: Record<string, string> = {
  // "von Bingen" reads as a particle surname but names the town she came from,
  // exactly like "of Norwich". She is universally filed under Hildegard.
  'hildegard von bingen': 'Hildegard',
  // Unhyphenated compound surname: "Vaughan Williams" is one name and files
  // under V. Nothing in the string marks it as compound rather than a middle
  // name + surname, so no rule can tell it from "Alice Archer Sewall" (→ S).
  'ursula vaughan williams': 'Vaughan Williams',
  // Added on the port to the hymnal (2026-08-09). Ralph is the single most
  // credited person in The Hymnal 1982 — composer, arranger or harmonizer on
  // more than fifteen hymns — so filing him under W rather than V is the most
  // visible sorting error the /people index could make. Same compound surname as
  // Ursula, and undecidable by the same rule.
  'ralph vaughan williams': 'Vaughan Williams',
}

/**
 * The name to alphabetize under, e.g.
 *   "Amy Beach"                   → "Beach"
 *   "Patricia Van Ness"           → "Van Ness"   (anglicized: capitalized particle)
 *   "Margaretha Christina de Jong"→ "Jong"       (Dutch: lowercase particle)
 *   "Julian of Norwich"           → "Julian"     (toponymic)
 *   "Kassia"                      → "Kassia"     (mononym)
 */
export function sortNameFor(fullName: string): string {
  const name = fullName.trim().replace(/\s+/g, ' ')
  if (!name) return ''

  const irregular = IRREGULAR[name.toLowerCase()]
  if (irregular) return irregular

  let tokens = name.split(' ')

  // Drop honorifics ("Mother Teresa of Calcutta" → "Teresa of Calcutta").
  while (tokens.length > 1 && TITLES.has(tokens[0].toLowerCase())) tokens = tokens.slice(1)

  // Drop generational suffixes.
  while (tokens.length > 1 && SUFFIXES.has(tokens[tokens.length - 1].toLowerCase().replace(/,$/, ''))) {
    tokens = tokens.slice(0, -1)
  }

  if (tokens.length === 1) return tokens[0]

  // Toponymic: "X of Y" sorts under X, the given name.
  const ofAt = tokens.findIndex((t) => t.toLowerCase() === 'of')
  if (ofAt > 0) return tokens.slice(0, ofAt).join(' ')

  // Particle surname: find the last particle that still has a name after it.
  let particleAt = -1
  for (let i = tokens.length - 2; i >= 1; i--) {
    if (PARTICLES.has(tokens[i].toLowerCase())) {
      particleAt = i
      break
    }
  }
  if (particleAt === -1) return tokens[tokens.length - 1]

  // Capitalized particle → anglicized, it belongs in the key ("Van Ness").
  // Lowercase particle → continental, file under what follows ("Jong").
  const isAnglicized = /^[A-Z]/.test(tokens[particleAt])
  return tokens.slice(isAnglicized ? particleAt : particleAt + 1).join(' ')
}

/** The A–Z heading a name files under ("Van Ness" → "V"). */
export function sortLetterFor(fullName: string): string {
  return (sortNameFor(fullName)[0] ?? '#').toUpperCase()
}

/** Locale-aware comparator over derived sort names, with the full name as tiebreak. */
export function compareBySortName(a: string, b: string): number {
  return sortNameFor(a).localeCompare(sortNameFor(b), 'en', { sensitivity: 'base' }) || a.localeCompare(b)
}
